import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Card, Row, Col, Button, Table, Tag, Space, Modal, Form, Input, Select, Spin, Alert } from 'antd';
import { ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNetworks, useCreateNetwork, useUpdateNetwork, useDeleteNetwork } from '../hooks/useNetworkManagement';
import { useCoins } from '../hooks/useCoinManagement';
import { showSuccessNotification, showErrorNotification, showWarningNotification } from '../utils/notificationUtils';
import SearchableSelect from '../components/common/SearchableSelect';

const { Title } = Typography;
const { Option } = Select;

const NetworksManagementPage: React.FC = () => {
  const { t } = useTranslation('networks');
  const { t: statusT } = useTranslation('status');
  const { t: commonT } = useTranslation('common');
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState<any>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [testnetFilter, setTestnetFilter] = useState<string>('');
  const [filteredNetworks, setFilteredNetworks] = useState<any[]>([]);

  // Fetch networks data
  const {
    data: networksData,
    isLoading: isLoadingNetworks,
    isError: isNetworksError,
    refetch: refetchNetworks
  } = useNetworks();

  // Fetch coins data for base coin selection
  const {
    data: coinsData,
    isLoading: isLoadingCoins,
    isError: isCoinsError
  } = useCoins();

  // Mutations for CRUD operations
  const createNetworkMutation = useCreateNetwork();
  const updateNetworkMutation = useUpdateNetwork();
  const deleteNetworkMutation = useDeleteNetwork();

  // Log network data for debugging
  useEffect(() => {
    if (networksData) {
      console.log('Raw networks data:', networksData);
    }
  }, [networksData]);

  // Format networks data for table using useMemo to avoid re-creating on every render
  const networks = useMemo(() => {
    console.log('Processing networks data:', networksData);

    if (!networksData) return [];

    // Handle different response formats
    let networksList: any[] = [];

    // Handle array format directly
    if (Array.isArray(networksData)) {
      networksList = networksData;
    }
    // Handle object with data property (standard API response)
    else if (networksData.data && Array.isArray(networksData.data)) {
      networksList = networksData.data;
    }
    // Handle object with networks property (some API responses)
    else if (networksData.networks && Array.isArray(networksData.networks)) {
      networksList = networksData.networks;
    }
    // If we can't determine the format, return empty array
    else {
      console.error('Unknown network data format:', networksData);
      return [];
    }

    console.log('Processed networks list:', networksList);

    // Map the networks list to the format needed for the table
    return networksList.map((network: any) => {
      // Find the coin by ID to get its symbol for display
      const coinId = network.base_coin?._id || network.base_coin;
      const coin = coinsData?.find((c: any) => (c._id || c.id) === coinId);
      const coinSymbol = coin ? coin.symbol : (network.base_coin?.symbol || coinId);

      // Convert "Yes"/"No" strings to boolean for internal use
      const isEvm = network.is_evn === "Yes" || network.is_evn === true;
      const isTestnet = network.is_testnet === "Yes" || network.is_testnet === true;

      return {
        key: network._id || network.id,
        id: network._id || network.id,
        title: network.title,
        full_name: network.full_name,
        is_evn: isEvm, // Boolean for internal use
        is_evn_display: isEvm ? 'Yes' : 'No', // For display purposes
        is_testnet: isTestnet, // Boolean for internal use
        is_testnet_display: isTestnet ? 'Yes' : 'No', // For display purposes
        status: network.status,
        base_coin: coinId, // Keep the ID for internal use
        base_coin_symbol: coinSymbol, // Add symbol for display
        net_fee: network.net_fee,
        rpc: network.rpc,
        explorer: network.explorer
      };
    });
  }, [networksData, coinsData]);

  // Filter networks based on search text and dropdown filters
  useEffect(() => {
    let filtered = networks;

    // Apply search text filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((network: any) => (
        (network.title || '').toLowerCase().includes(searchLower) ||
        (network.full_name || '').toLowerCase().includes(searchLower) ||
        (network.base_coin_symbol && network.base_coin_symbol.toLowerCase().includes(searchLower))
      ));
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter((network: any) => network.status === statusFilter);
    }

    // Apply testnet filter
    if (testnetFilter) {
      const isTestnetFilter = testnetFilter === 'Yes';
      filtered = filtered.filter((network: any) => network.is_testnet === isTestnetFilter);
    }

    setFilteredNetworks(filtered);
  }, [networks, searchText, statusFilter, testnetFilter]);

  // Format coins data for selection
  const coins = coinsData?.map((coin: any) => ({
    id: coin._id || coin.id,
    symbol: coin.symbol,
    full_name: coin.full_name
  })) || [];

  // Table columns
  const columns = [
    {
      title: t('network.title'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('network.fullName'),
      dataIndex: 'full_name',
      key: 'full_name',
    },
    {
      title: t('network.baseCoin'),
      dataIndex: 'base_coin_symbol',
      key: 'base_coin',
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: t('network.isEVM'),
      dataIndex: 'is_evn_display',
      key: 'is_evn',
      render: (isEVM: string) => (
        isEVM === 'Yes' ? <Tag color="green">{commonT('actions.yes', 'Yes')}</Tag> : <Tag color="orange">{commonT('actions.no', 'No')}</Tag>
      )
    },
    {
      title: t('network.isTestnet', 'Is Testnet'),
      dataIndex: 'is_testnet_display',
      key: 'is_testnet',
      render: (isTestnet: string) => (
        isTestnet === 'Yes' ? <Tag color="purple">{commonT('actions.yes', 'Yes')}</Tag> : <Tag color="blue">{commonT('actions.no', 'No')}</Tag>
      )
    },
    {
      title: t('network.netFee'),
      dataIndex: 'net_fee',
      key: 'net_fee',
    },
    {
      title: t('network.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'Active' ? 'green' : 'red';
        return <Tag color={color}>{statusT(status.toLowerCase(), status)}</Tag>;
      }
    },
    {
      title: commonT('actions.title'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {commonT('actions.edit')}
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            {commonT('actions.delete')}
          </Button>
        </Space>
      )
    }
  ];

  // Handle adding a new network
  const handleAdd = () => {
    setIsAddModalVisible(true);
    form.resetFields();
  };

  // Handle editing a network
  const handleEdit = (network: any) => {
    setSelectedNetwork(network);
    setIsEditModalVisible(true);

    // Find the coin by ID to get its symbol
    const coinId = network.base_coin;
    const coin = coinsData?.find((c: any) => (c._id || c.id) === coinId);
    const coinSymbol = coin ? coin.symbol : coinId; // Fallback to ID if coin not found

    // Convert "Yes"/"No" strings to boolean values for form
    const isEvm = network.is_evn === "Yes" || network.is_evn === true;
    const isTestnet = network.is_testnet === "Yes" || network.is_testnet === true;

    form.setFieldsValue({
      title: network.title,
      full_name: network.full_name,
      is_evn: isEvm,
      is_testnet: isTestnet,
      status: network.status,
      base_coin: coinSymbol, // Use symbol instead of ID
      net_fee: network.net_fee,
      rpc: network.rpc,
      explorer: network.explorer
    });
  };

  // Handle deleting a network
  const handleDelete = (network: any) => {
    Modal.confirm({
      title: t('network.confirmDelete'),
      content: t('network.deleteWarning', { title: network.title }),
      okText: commonT('actions.delete'),
      okType: 'danger',
      cancelText: commonT('actions.cancel'),
      onOk: async () => {
        try {
          await deleteNetworkMutation.mutateAsync(network.id);
          showSuccessNotification(t('network.deleteSuccess', 'Network deleted successfully'));
        } catch (error: any) {
          console.error('Error deleting network:', error);

          // Check for dependency errors
          if (error.response && error.response.data && error.response.data.error) {
            const errorMessage = error.response.data.error.message || '';

            // Check if error is related to dependencies (coin-network relationships)
            if (errorMessage.includes('Cannot delete network') ||
              errorMessage.includes('coin-network relationship')) {
              // Show specific error for dependencies
              showWarningNotification(
                t('network.dependencyError', 'This network cannot be deleted because it is used in other parts of the system.'),
                t('network.markInactiveInstead', 'Consider marking it as inactive instead.')
              );

              // Ask if user wants to mark as inactive instead
              Modal.confirm({
                title: t('network.markInactiveTitle', 'Mark as Inactive?'),
                content: t('network.markInactiveContent', 'Would you like to mark this network as inactive instead of deleting it?'),
                okText: commonT('actions.yes'),
                cancelText: commonT('actions.no'),
                onOk: async () => {
                  try {
                    // Update the network status to inactive
                    await updateNetworkMutation.mutateAsync({
                      id: network.id,
                      networkData: {
                        ...network,
                        status: 'Inactive',
                        creator_id: 123 // Using a hardcoded numeric value as required by the model
                      }
                    });
                    showSuccessNotification(t('network.markedInactive', 'Network marked as inactive successfully'));
                  } catch (updateError) {
                    console.error('Error updating network status:', updateError);
                    showErrorNotification(t('network.updateError', 'Failed to update network status'));
                  }
                }
              });
              return;
            }
          }

          // Generic error message for other errors
          showErrorNotification(t('network.deleteError', 'Failed to delete network'));
        }
      }
    });
  };

  // Handle form submission for adding a network
  const handleAddFormSubmit = async (values: any) => {
    try {
      // Find the selected coin to get its ID
      const selectedCoin = coinsData?.find((coin: any) => coin.symbol === values.base_coin);

      if (!selectedCoin) {
        showErrorNotification(
          t('network.addError', 'Failed to add network'),
          t('network.baseCoinNotFound', 'Selected base coin not found')
        );
        return;
      }

      // Convert net_fee to a number as required by the API
      // Convert is_evn and is_testnet to "Yes" or "No" as required by the database model
      const networkData = {
        ...values,
        base_coin: selectedCoin._id || selectedCoin.id, // Use the coin's ID instead of symbol
        is_evn: values.is_evn === true ? "Yes" : "No", // Convert boolean to "Yes" or "No" string
        is_testnet: values.is_testnet === true ? "Yes" : "No", // Convert boolean to "Yes" or "No" string
        net_fee: values.net_fee ? Number(values.net_fee) : 0, // Convert to number
        creator_id: 123 // Using a hardcoded numeric value as required by the model
      };

      console.log('Submitting network data:', networkData);
      await createNetworkMutation.mutateAsync(networkData);
      showSuccessNotification(t('network.addSuccess', 'Network added successfully'));
      setIsAddModalVisible(false);
    } catch (error: any) {
      console.error('Error adding network:', error);

      // Show more detailed error message if available
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error.message || '';
        const errorDetails = error.response.data.error.details?.errors || [];

        // Create a detailed error message
        let detailedMessage = errorMessage;
        if (Array.isArray(errorDetails)) {
          if (errorDetails.length > 0) {
            detailedMessage += ': ' + errorDetails.join(', ');
          }
        } else if (typeof errorDetails === 'object') {
          // Handle case where errorDetails is an object with nested error details
          const detailsArray = [];
          for (const key in errorDetails) {
            if (errorDetails[key].message) {
              detailsArray.push(errorDetails[key].message);
            }
          }
          if (detailsArray.length > 0) {
            detailedMessage += ': ' + detailsArray.join(', ');
          }
        }

        showErrorNotification(
          t('network.addError', 'Failed to add network'),
          detailedMessage
        );
      } else {
        showErrorNotification(t('network.addError', 'Failed to add network'));
      }
    }
  };

  // Handle form submission for editing a network
  const handleEditFormSubmit = async (values: any) => {
    try {
      // Find the selected coin to get its ID
      const selectedCoin = coinsData?.find((coin: any) => coin.symbol === values.base_coin);

      if (!selectedCoin) {
        showErrorNotification(
          t('network.updateError', 'Failed to update network'),
          t('network.baseCoinNotFound', 'Selected base coin not found')
        );
        return;
      }

      // Convert net_fee to a number as required by the API
      // Convert is_evn and is_testnet to "Yes" or "No" as required by the database model
      const networkData = {
        ...values,
        base_coin: selectedCoin._id || selectedCoin.id, // Use the coin's ID instead of symbol
        is_evn: values.is_evn === true ? "Yes" : "No", // Convert boolean to "Yes" or "No" string
        is_testnet: values.is_testnet === true ? "Yes" : "No", // Convert boolean to "Yes" or "No" string
        net_fee: values.net_fee ? Number(values.net_fee) : 0, // Convert to number
        creator_id: 123 // Using a hardcoded numeric value as required by the model
      };

      console.log('Submitting updated network data:', networkData);
      await updateNetworkMutation.mutateAsync({
        id: selectedNetwork.id,
        networkData: networkData
      });
      showSuccessNotification(t('network.updateSuccess', 'Network updated successfully'));
      setIsEditModalVisible(false);
    } catch (error: any) {
      console.error('Error updating network:', error);

      // Show more detailed error message if available
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error.message || '';
        const errorDetails = error.response.data.error.details?.errors || [];

        // Create a detailed error message
        let detailedMessage = errorMessage;
        if (Array.isArray(errorDetails)) {
          if (errorDetails.length > 0) {
            detailedMessage += ': ' + errorDetails.join(', ');
          }
        } else if (typeof errorDetails === 'object') {
          // Handle case where errorDetails is an object with nested error details
          const detailsArray = [];
          for (const key in errorDetails) {
            if (errorDetails[key].message) {
              detailsArray.push(errorDetails[key].message);
            }
          }
          if (detailsArray.length > 0) {
            detailedMessage += ': ' + detailsArray.join(', ');
          }
        }

        showErrorNotification(
          t('network.updateError', 'Failed to update network'),
          detailedMessage
        );
      } else {
        showErrorNotification(t('network.updateError', 'Failed to update network'));
      }
    }
  };

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={2}>{t('title', 'Networks Management')}</Title>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder={t('search', 'Search networks')}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <SearchableSelect
              placeholder={t('network.filterByStatus', 'Filter by Status')}
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
              allowClear
            >
              <Option value="Active">{statusT('active', 'Active')}</Option>
              <Option value="Inactive">{statusT('inactive', 'Inactive')}</Option>
            </SearchableSelect>
            <SearchableSelect
              placeholder={t('network.filterByTestnet', 'Filter by Testnet')}
              value={testnetFilter}
              onChange={setTestnetFilter}
              style={{ width: 150 }}
              allowClear
            >
              <Option value="Yes">{commonT('actions.yes', 'Yes')}</Option>
              <Option value="No">{commonT('actions.no', 'No')}</Option>
            </SearchableSelect>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetchNetworks()}
              loading={isLoadingNetworks}
            >
              {commonT('actions.refresh', 'Refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={isLoadingCoins}
            >
              {t('addNetwork', 'Add Network')}
            </Button>
          </Space>
        </Col>
      </Row>

      {isNetworksError && (
        <Alert
          message={t('network.errorTitle', 'Error Loading Networks')}
          description={t('network.errorDescription', 'There was an error loading the networks data. Please try refreshing the page.')}
          type="error"
          showIcon
          style={{ marginBottom: 16, marginTop: 16 }}
        />
      )}

      <Card style={{ marginTop: 16 }}>
        {isLoadingNetworks ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredNetworks}
            pagination={{ pageSize: 10 }}
            rowKey="key"
            locale={{
              emptyText: searchText || statusFilter || testnetFilter
                ? t('noNetworksFound', 'No networks match your filters')
                : t('noNetworks', 'No networks found')
            }}
          />
        )}
      </Card>

      {/* Modal for adding a new network */}
      <Modal
        title={t('addNetwork', 'Add Network')}
        open={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        footer={null}
        width={700}
        confirmLoading={createNetworkMutation.isPending}
      >
        {isLoadingCoins ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              {t('network.loadingCoins', 'Loading coins data...')}
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddFormSubmit}
            disabled={createNetworkMutation.isPending || isCoinsError}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="title"
                  label={t('network.title')}
                  rules={[{ required: true, message: t('network.titleRequired') }]}
                >
                  <Input placeholder={t('network.titlePlaceholder', 'e.g. ETH')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="full_name"
                  label={t('network.fullName')}
                  rules={[{ required: true, message: t('network.fullNameRequired') }]}
                >
                  <Input placeholder={t('network.fullNamePlaceholder', 'e.g. ETHEREUM')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="base_coin"
                  label={t('network.baseCoin')}
                  rules={[{ required: true, message: t('network.baseCoinRequired') }]}
                >
                  <Select
                    placeholder={t('network.selectBaseCoin')}
                    loading={isLoadingCoins}
                    disabled={isLoadingCoins || isCoinsError}
                  >
                    {coins.map((coin: any) => (
                      <Option key={coin.id} value={coin.symbol}>
                        {coin.symbol} - {coin.full_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="is_evn"
                  label={t('network.isEVM')}
                  rules={[{ required: true, message: t('network.isEVMRequired') }]}
                >
                  <Select placeholder={t('network.selectIsEVM')}>
                    <Option value={true}>{commonT('actions.yes', 'Yes')}</Option>
                    <Option value={false}>{commonT('actions.no', 'No')}</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="is_testnet"
                  label={t('network.isTestnet', 'Is Testnet')}
                  rules={[{ required: true, message: t('network.isTestnetRequired', 'Please select if this is a testnet') }]}
                >
                  <Select placeholder={t('network.selectIsTestnet', 'Select if testnet')}>
                    <Option value={true}>{commonT('actions.yes', 'Yes')}</Option>
                    <Option value={false}>{commonT('actions.no', 'No')}</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="status"
                  label={t('network.status')}
                  rules={[{ required: true, message: t('network.statusRequired') }]}
                >
                  <Select placeholder={t('network.selectStatus')}>
                    <Option value="Active">{statusT('active', 'Active')}</Option>
                    <Option value="Inactive">{statusT('inactive', 'Inactive')}</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="net_fee"
                  label={t('network.netFee')}
                  rules={[
                    { required: true, message: t('network.netFeeRequired') },
                    {
                      validator: (_, value) => {
                        if (value && isNaN(Number(value))) {
                          return Promise.reject(new Error(t('network.netFeeMustBeNumber', 'Network fee must be a number')));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Input
                    type="text"
                    placeholder={t('network.netFeePlaceholder', 'e.g. 0.001')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="rpc"
                  label={t('network.rpc')}
                  rules={[{ required: true, message: t('network.rpcRequired') }]}
                >
                  <Input placeholder={t('network.rpcPlaceholder', 'e.g. https://ethereum.publicnode.com')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="explorer"
                  label={t('network.explorer')}
                >
                  <Input placeholder={t('network.explorerPlaceholder', 'e.g. https://etherscan.io')} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
                {commonT('actions.submit', 'Submit')}
              </Button>
              <Button onClick={() => setIsAddModalVisible(false)}>
                {commonT('actions.cancel', 'Cancel')}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Modal for editing a network */}
      <Modal
        title={t('editNetwork', 'Edit Network')}
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={700}
        confirmLoading={updateNetworkMutation.isPending}
      >
        {isLoadingCoins ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              {t('network.loadingCoins', 'Loading coins data...')}
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleEditFormSubmit}
            disabled={updateNetworkMutation.isPending || isCoinsError}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="title"
                  label={t('network.title')}
                  rules={[{ required: true, message: t('network.titleRequired') }]}
                >
                  <Input placeholder={t('network.titlePlaceholder', 'e.g. ETH')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="full_name"
                  label={t('network.fullName')}
                  rules={[{ required: true, message: t('network.fullNameRequired') }]}
                >
                  <Input placeholder={t('network.fullNamePlaceholder', 'e.g. ETHEREUM')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="base_coin"
                  label={t('network.baseCoin')}
                  rules={[{ required: true, message: t('network.baseCoinRequired') }]}
                >
                  <Select
                    placeholder={t('network.selectBaseCoin')}
                    loading={isLoadingCoins}
                    disabled={isLoadingCoins || isCoinsError}
                  >
                    {coins.map((coin: any) => (
                      <Option key={coin.id} value={coin.symbol}>
                        {coin.symbol} - {coin.full_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="is_evn"
                  label={t('network.isEVM')}
                  rules={[{ required: true, message: t('network.isEVMRequired') }]}
                >
                  <Select placeholder={t('network.selectIsEVM')}>
                    <Option value={true}>{commonT('actions.yes', 'Yes')}</Option>
                    <Option value={false}>{commonT('actions.no', 'No')}</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="is_testnet"
                  label={t('network.isTestnet', 'Is Testnet')}
                  rules={[{ required: true, message: t('network.isTestnetRequired', 'Please select if this is a testnet') }]}
                >
                  <Select placeholder={t('network.selectIsTestnet', 'Select if testnet')}>
                    <Option value={true}>{commonT('actions.yes', 'Yes')}</Option>
                    <Option value={false}>{commonT('actions.no', 'No')}</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="status"
                  label={t('network.status')}
                  rules={[{ required: true, message: t('network.statusRequired') }]}
                >
                  <Select placeholder={t('network.selectStatus')}>
                    <Option value="Active">{statusT('active', 'Active')}</Option>
                    <Option value="Inactive">{statusT('inactive', 'Inactive')}</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="net_fee"
                  label={t('network.netFee')}
                  rules={[
                    { required: true, message: t('network.netFeeRequired') },
                    {
                      validator: (_, value) => {
                        if (value && isNaN(Number(value))) {
                          return Promise.reject(new Error(t('network.netFeeMustBeNumber', 'Network fee must be a number')));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Input
                    type="text"
                    placeholder={t('network.netFeePlaceholder', 'e.g. 0.001')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="rpc"
                  label={t('network.rpc')}
                  rules={[{ required: true, message: t('network.rpcRequired') }]}
                >
                  <Input placeholder={t('network.rpcPlaceholder', 'e.g. https://ethereum.publicnode.com')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="explorer"
                  label={t('network.explorer')}
                >
                  <Input placeholder={t('network.explorerPlaceholder', 'e.g. https://etherscan.io')} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
                {commonT('actions.submit', 'Submit')}
              </Button>
              <Button onClick={() => setIsEditModalVisible(false)}>
                {commonT('actions.cancel', 'Cancel')}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default NetworksManagementPage;
